import { useMemo } from 'react';
import { useMantineTheme, Box } from '@mantine/core';
import { getIconByName, IconName, IconRegistry } from '../../../config/icon-map';
import { iconSizes, IconVariant, resolveIconColor } from '../../../theme/icons';
import { isRTL } from '../../../utils/env';

export interface IconProps {
  name: IconName | string;
  size?: keyof typeof iconSizes | number;
  color?: string;
  variant?: IconVariant;
  spin?: boolean;
  rotate?: number;
  'aria-label'?: string;
  title?: string;
  mirrorInRtl?: boolean;
  registry?: IconRegistry;
  className?: string;
  style?: React.CSSProperties;
}

function Icon({
  name,
  size = 'md',
  color,
  variant = 'default',
  spin = false,
  rotate = 0,
  mirrorInRtl = true,
  registry,
  className,
  style,
  'aria-label': ariaLabel,
  title,
}: IconProps) {
  const theme = useMantineTheme();
  const Component = useMemo(() => getIconByName(name, registry), [name, registry]);

  const resolvedSize = typeof size === 'number' ? size : iconSizes[size] ?? iconSizes.md;
  const resolvedColor = resolveIconColor(theme, color, variant);

  const isRtl = mirrorInRtl && isRTL();
  const transform = [isRtl ? 'scaleX(-1)' : null, rotate ? `rotate(${rotate}deg)` : null]
    .filter(Boolean)
    .join(' ');

  const ariaHidden = ariaLabel ? undefined : true;

  // Build icon styles for the wrapper span
  const iconWrapperStyle: React.CSSProperties = {
    display: 'block',
    lineHeight: 0,
    animation: spin ? 'icon-spin 0.9s linear infinite' : undefined,
    transform: transform || undefined,
  };

  return (
    <Box
      component="span"
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      title={title}
      role={ariaHidden ? 'presentation' : 'img'}
    >
      <span style={iconWrapperStyle}>
        <Component
          size={resolvedSize}
          color={resolvedColor}
          stroke={variant === 'muted' ? 1.25 : 1.5}
        />
      </span>
    </Box>
  );
}

Icon.displayName = 'Icon';

export default Icon;
export { Icon };
export type { IconVariant };
